import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot,
  X,
  Send,
  Activity,
  ShieldCheck,
  Terminal,
  Cpu
} from "lucide-react";
import "./AIChatbot.css";

const AIChatbot = ({ isStandalone = false }) => {
  const [isOpen, setIsOpen] = useState(isStandalone);
  const [message, setMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const chatBodyRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (isStandalone) setIsOpen(true);
  }, [isStandalone]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const suggestedQuestions = [
    { id: "sensors", text: "SYNC_HARDWARE_PROTOCOLS" },
    { id: "security", text: "AUDIT_DATA_SOVEREIGNTY" },
    { id: "vitals", text: "MONITOR_REALTIME_VITALS" },
    { id: "appointments", text: "RESERVE_CLINICAL_SESSION" },
    { id: "integration", text: "IOT_SENSOR_INTEGRATION" },
    { id: "emergency", text: "EMERG_ALERT_DYNAMICS" },
    { id: "help", text: "ACCESS_HELP_SYSTEM" }
  ];

  const knowledgeBase = {
    start: "SYSTEM_INITIALIZED: Navigate to the 'Devices' registry to link your IoT nodes. Follow the Sovereign Onboarding Protocol to map your identity.",
    sensors: "HARDWARE_STATUS: Medico.in synchronizes with MAX30102 (SpO2/HR), AD8232 (ECG), DS18B20 (Temp), and MPU6050 (Motion) sensors.",
    security: "PROTOCOL_VERIFIED: All clinical data is encrypted via AES-256. Medico.in ensures 100% Data Sovereignty for every clinical node.",
    vitals: "TELEMETRY_SYNC: Real-time vitals are streamed via Secure WebSockets and mapped to the Patient Dashboard with sub-second latency.",
    appointments: "SESSION_REGISTRY: Access the 'Appointments' hub to provision a clinical session with verified physicians in your network.",
    integration: "INTEGRATION_LOGIC: Use the provided Arduino/ESP32 firmware libraries to synchronize your physical hardware with the Sovereign Hub.",
    emergency: "CRITICAL_OVERSIGHT: High-priority alerts are triggered when vitals cross threshold nodes. Doctors are notified via real-time relay.",
    max30102: "SENSOR_NODE: MAX30102 measures blood oxygen and heart rate. Ensure the sensor is flush against the skin for noise reduction.",
    ad8232: "SENSOR_NODE: AD8232 is a Lead-I ECG monitor. Position electrodes on the right arm, left arm, and right leg for optimal wave acquisition.",
    ds18b20: "SENSOR_NODE: DS18B20 measures high-precision body temperature over a single-wire interface.",
    ecg: "WAVEFORM_ANALYSIS: The AD8232 module provisioned on the hub translates electrical heart signals into real-time clinical waveforms.",
    spo2: "OXYGEN_SATURATION: SpO2 nodes monitor the percentage of oxygen in the blood. Normal clinical levels are between 95-100%.",
    heartrate: "CADENCE_OVERSIGHT: Heart rate nodes track beats per minute (BPM) in real-time, feeding the dashboard's kinetic charts.",
    firebase: "BACKEND_HUB: Medico.in utilizes Firebase Realtime Database for zero-latency clinical data synchronization.",
    arduino: "FIRMWARE_LOGIC: Arduino-compatible sketches are available in the 'Hardware Documentation' section for quick prototyping.",
    esp32: "CONNECTIVITY_NODE: ESP32 provides the WiFi/Bluetooth bridge needed to relay IoT sensor data to the Sovereign Hub.",
    caregiver: "OVERSIGHT_LINK: Patients can link 'Caregiver' identities to their profile for external monitoring and emergency authorization.",
    doctors: "PHYSICIAN_REGISTRY: All doctors on the platform are verified clinical identities with specific medical specialization nodes.",
    hospital: "CLINICAL_FACILITY: Hospital identities can manage a network of doctors and oversight multiple patient streams simultaneously.",
    alerts: "PROTOCOL_NOTIFICATION: Alerts are categorized as NORMAL, WARNING, or CRITICAL based on sensor telemetry analysis.",
    encryption: "DATA_SHIELD: End-to-end encryption is active for all patient-doctor communication channels.",
    privacy: "SOVEREIGN_RIGHTS: Your clinical data belongs to you. Medico.in acts only as a secure relay and oversight hub.",
    hipaa: "COMPLIANCE_STATUS: Medico.in architectural protocols are designed to align with international HIPAA and GDPR standards.",
    medication: "PRESCRIPTION_DATA: View active medication nodes and dosage timing in the 'Medical Records' section of your dashboard.",
    history: "TEMPORAL_AUDIT: Historical vitals data is archived in the Sovereign Registry for longitudinal clinical analysis.",
    profile: "IDENTITY_NODE: Update your clinical metrics and emergency contact data in the 'Settings' section.",
    auth: "SESSION_SECURITY: Multi-factor clinical authentication is required to access sensitive patient health nodes.",
    documentation: "TECHNICAL_HUB: Access the 'Documentation' portal for API specifications and hardware wiring diagrams.",
    mobile: "RESPONSIVE_HUD: The Medico.in interface is fully responsive, optimized for mobile oversight and 4K displays.",
    api: "DATA_RELAY: REST and WebSocket APIs are available for industrial clinical enterprise integration.",
    latency: "PERFORMANCE_NODE: Our sync engine operates with <200ms latency to ensure real-time clinical responsiveness.",
    help: "Sovereign AI Hub Help: You can ask about [Sensors] (MAX30102, AD8232, ECG, SpO2), [Protocols] (Security, Encryption, Data Sovereignty, HIPAA), or [Platform] (Appointments, Caregiver, Vitals, Alerts, History).",
    default: "QUERY_UNRESOLVED: Please consult the technical hub for specific clinical integration protocols."
  };

  const handleSendMessage = (text) => {
    if (!text.trim()) return;

    const userMsg = { id: Date.now(), type: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setMessage("");
    setIsTyping(true);

    setTimeout(() => {
      const lowerText = text.toLowerCase();
      let botResponse = knowledgeBase.default;
      
      // Dynamic Keyword Matching Logic
      if (lowerText.includes("help") || lowerText.includes("assist") || lowerText.includes("command")) {
        botResponse = knowledgeBase.help;
      } else {
        for (const [key, value] of Object.entries(knowledgeBase)) {
          if (lowerText.includes(key)) {
            botResponse = value;
            break;
          }
        }
      }

      setMessages(prev => [...prev, { id: Date.now() + 1, type: "bot", content: botResponse }]);
      setIsTyping(false);
    }, 1200);
  };

  if (!isStandalone && location.pathname === "/auth") return null;

  return (
    <div className={`aichat-wrapper ${isStandalone ? "standalone" : ""}`}>
      <AnimatePresence>
        {!isStandalone && (
          <motion.button
            className="aichat-trigger"
            onClick={() => setIsOpen(!isOpen)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            whileHover={{ scale: 1.1, y: -5 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
          >
            {isOpen ? <X size={24} /> : <Bot size={24} />}
            <div className="aichat-status-led"></div>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="aichat-window"
            initial={isStandalone ? { opacity: 0 } : { scale: 0.8, opacity: 0, y: 100, x: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0, x: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 100, x: 50 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
          >
            <div className="aichat-crt-overlay"></div>

            <header className="aichat-header">
              <div className="aichat-identity">
                <div className="aichat-avatar">
                  <Cpu size={18} className="icon-pulse" />
                </div>
                <div className="aichat-id-text">
                  <h3>MEDICO_CORE_AI</h3>
                  <p>AI_READY</p>
                </div>
              </div>
              {!isStandalone && (
                <button className="aichat-close" onClick={() => setIsOpen(false)}>
                  <X size={20} />
                </button>
              )}
            </header>

            <div className="aichat-body" ref={chatBodyRef}>
              {messages.length === 0 ? (
                <div className="aichat-intro">
                  <motion.div
                    initial={{ rotate: -10, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Terminal size={48} className="icon-dim" />
                  </motion.div>
                  <div className="aichat-greeting">
                    <p>IDENTITY_RECOGNIZED. Initialization sequence complete.</p>
                    <p>Awaiting query on clinical protocols...</p>
                  </div>

                  <div className="aichat-suggestions">
                    {suggestedQuestions.map(q => (
                      <button
                        key={q.id}
                        className="aichat-suggestion-btn"
                        onClick={() => handleSendMessage(q.text)}
                      >
                        {q.text}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="aichat-messages">
                  {messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      className={`aichat-msg-row ${msg.type}`}
                      initial={{ opacity: 0, x: msg.type === "user" ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      <div className="aichat-bubble">
                        {msg.content}
                      </div>
                    </motion.div>
                  ))}
                  {isTyping && (
                    <motion.div
                      className="aichat-msg-row bot"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <div className="aichat-bubble">
                        <div className="aichat-typing-indicator">
                          <span></span><span></span><span></span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
            </div>

            <footer className="aichat-footer">
              <div className="aichat-input-box">
                <input
                  type="text"
                  placeholder="INPUT_COMMAND_TEXT..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage(message)}
                />
              </div>
              <button className="aichat-send-btn" onClick={() => handleSendMessage(message)}>
                <Send size={18} />
              </button>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIChatbot;
